import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
function ToolChip({
  icon,
  name
}: {
  icon: string;
  name: string;
}) {
  return <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white border border-neutral-200 text-sm font-medium text-[#2B211A] mx-1 align-middle shadow-sm" data-id="element-4713">
      <img src={`https://cdn.simpleicons.org/${icon}`} alt={name} className="w-3.5 h-3.5 object-contain" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${icon}.svg`;
    }} data-id="element-4714" />
      {name}
    </span>;
}
const AGENTS = [{
  id: 'cfo',
  name: 'Giga CFO',
  prompt: <>
        Reconcile this month's revenue from{' '}
        <ToolChip icon="stripe" name="Stripe" data-id="element-4715" /> into{' '}
        <ToolChip icon="quickbooks" name="QuickBooks" data-id="element-4716" />, then flag anything
        over budget in <ToolChip icon="slack" name="#finance" data-id="element-4717" />
      </>
}, {
  id: 'cmo',
  name: 'Giga CMO',
  prompt: <>
        When a new lead is added in <ToolChip icon="hubspot" name="HubSpot" data-id="element-4718" />,
        enrich the data with <ToolChip icon="clearbit" name="Clearbit" data-id="element-4719" />, and
        send a summary to <ToolChip icon="slack" name="#sales-leads" data-id="element-4720" />
      </>
}, {
  id: 'cto',
  name: 'Giga CTO',
  prompt: <>
        When a <ToolChip icon="pagerduty" name="PagerDuty" data-id="element-4721" /> alert fires, check
        recent <ToolChip icon="github" name="GitHub" data-id="element-4722" /> commits, and draft an
        incident report in <ToolChip icon="notion" name="Notion" data-id="element-4723" />
      </>
}, {
  id: 'design',
  name: 'Giga Head of Design',
  prompt: <>
        Turn the latest brief in <ToolChip icon="notion" name="Notion" data-id="element-4724" /> into a
        set of on-brand mockups in <ToolChip icon="figma" name="Figma" data-id="element-4725" />, then
        post them to <ToolChip icon="slack" name="#design-review" data-id="element-4726" />
      </>
}];
export function TaskletHome() {
  const [activeAgent, setActiveAgent] = useState(AGENTS[0]);
  return <div className="min-h-[90vh] bg-[#FAF8F4] font-body flex flex-col relative overflow-hidden pt-32 pb-20" data-id="element-4727">
      {/* Dot matrix background */}
      <div className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-4728" />

      {/* Subtle grain overlay */}
      <div className="absolute inset-0 opacity-[0.03] mix-blend-multiply pointer-events-none z-0" style={{
      backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")'
    }} data-id="element-4729"></div>

      {/* Hero Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 relative z-10" data-id="element-4730">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-4xl mx-auto w-full" data-id="element-4731">
          <div className="inline-flex items-center gap-2 mb-8" data-id="element-4732">
            <span className="text-[10px] font-bold tracking-widest text-[#EA580C] uppercase" data-id="element-4733">
              New
            </span>
            <span className="text-neutral-300" data-id="element-4734">·</span>
            <a href="#" className="text-sm font-medium text-[#2B211A] hover:text-[#EA580C] transition-colors flex items-center gap-1" data-id="element-4735">
              Giga for Teams <ArrowRight size={14} data-id="element-4736" />
            </a>
          </div>

          <h1 className="text-5xl md:text-7xl font-title font-medium text-[#2B211A] tracking-tight mb-6" data-id="element-4737">
            Agents that{' '}
            <span className="font-serif italic text-[#EA580C]" data-id="element-4738">
              own the work
            </span>
          </h1>

          <p className="text-lg md:text-xl text-neutral-500 mb-10 max-w-2xl mx-auto" data-id="element-4739">
            Your business co-pilot — a full team of Giga agents that manages
            every function, from finance to design.
          </p>

          {/* Prompt Box (Liquid Glass Style - Light Theme) */}
          <div className="relative bg-white/60 backdrop-blur-md rounded-[24px] border border-white shadow-xl shadow-black/5 p-6 md:p-8 max-w-3xl mx-auto text-left w-full flex flex-col" data-id="element-4740">
            {/* Agent Toggle Inside Chat Box */}
            <div className="flex flex-wrap items-center gap-2 mb-6 pb-6 border-b border-neutral-200/60" data-id="element-4741">
              <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mr-2" data-id="element-4742">
                Agent:
              </span>
              {AGENTS.map(agent => <button key={agent.id} onClick={() => setActiveAgent(agent)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeAgent.id === agent.id ? 'bg-white text-[#2B211A] shadow-sm border border-neutral-200' : 'text-neutral-500 hover:text-[#2B211A] hover:bg-white/50 border border-transparent'}`} data-id="element-4743">
                  {agent.name}
                </button>)}
            </div>

            <motion.div key={activeAgent.id} initial={{
            opacity: 0,
            y: 5
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="text-lg md:text-xl text-[#2B211A] leading-relaxed mb-12 min-h-[80px]" data-id="element-4744">
              {activeAgent.prompt}
              <span className="inline-block w-0.5 h-5 bg-[#EA580C] ml-1 animate-pulse align-middle" data-id="element-4745" />
            </motion.div>

            <div className="flex justify-end mt-auto" data-id="element-4746">
              <button className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-5 py-2.5 rounded-full text-sm font-bold transition-colors shadow-sm flex items-center gap-2" data-id="element-4747">
                Create Task <ArrowRight size={16} data-id="element-4748" />
              </button>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1,
      duration: 1
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-neutral-400" data-id="element-4749">
        <span className="text-[10px] font-bold tracking-widest uppercase" data-id="element-4750">
          Scroll to explore
        </span>
        <ChevronDown size={16} className="animate-bounce" data-id="element-4751" />
      </motion.div>
    </div>;
}